import { Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { adminProductsV3Service } from "../../services/adminProductsV3Service";
import { SubscriptionSettings } from "../../types/subscription";
import AdminModalShell from "./AdminModalShell";
import SubscriptionAccessEditor, { SubscriptionAccessType } from "./SubscriptionAccessEditor";

interface Provider {
  id: number;
  name: string;
}

interface ProviderItem {
  id: number;
  title: string;
  visibility: string;
}

interface AddInvestorSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (providerId: number, settings: SubscriptionSettings) => Promise<void>;
  providers: Provider[];
  existingProviderIds: number[];
}

const AddInvestorSubscriptionModal: React.FC<AddInvestorSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  providers,
  existingProviderIds,
}) => {
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [portfolioAccess, setPortfolioAccess] = useState<SubscriptionAccessType>("all");
  const [basketAccess, setBasketAccess] = useState<SubscriptionAccessType>("all");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedBasketIds, setSelectedBasketIds] = useState<number[]>([]);
  const [isExternal, setIsExternal] = useState(true);
  const [providerPortfolios, setProviderPortfolios] = useState<ProviderItem[]>([]);
  const [providerBaskets, setProviderBaskets] = useState<ProviderItem[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const availableProviders = useMemo(
    () => providers.filter((provider) => !existingProviderIds.includes(provider.id)),
    [existingProviderIds, providers]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedProviderId(null);
    setPortfolioAccess("all");
    setBasketAccess("all");
    setSelectedPortfolioIds([]);
    setSelectedBasketIds([]);
    setIsExternal(true);
    setProviderPortfolios([]);
    setProviderBaskets([]);
    setSubmitError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setPortfolioAccess("all");
    setBasketAccess("all");
    setSelectedPortfolioIds([]);
    setSelectedBasketIds([]);
    setSubmitError(null);
  }, [isOpen, selectedProviderId]);

  useEffect(() => {
    if (!selectedProviderId) {
      setProviderPortfolios([]);
      setProviderBaskets([]);
      setContentLoading(false);
      return;
    }

    let active = true;
    setProviderPortfolios([]);
    setProviderBaskets([]);

    const loadContent = async () => {
      try {
        setContentLoading(true);
        const [portfolios, baskets] = await Promise.all([
          adminProductsV3Service.listAllPortfolios(selectedProviderId),
          adminProductsV3Service.listAllBaskets(selectedProviderId),
        ]);

        if (!active) return;
        setProviderPortfolios(
          portfolios
            .filter((portfolio) => portfolio.visibility === "SUBSCRIBER")
            .map((portfolio) => ({
              id: portfolio.id,
              title: portfolio.title,
              visibility: portfolio.visibility,
            }))
        );
        setProviderBaskets(
          baskets
            .filter((basket) => basket.visibility === "SUBSCRIBER")
            .map((basket) => ({
              id: basket.id,
              title: basket.title,
              visibility: basket.visibility,
            }))
        );
      } catch (err) {
        console.error("Failed to load provider products:", err);
        if (active) {
          setProviderPortfolios([]);
          setProviderBaskets([]);
        }
      } finally {
        if (active) setContentLoading(false);
      }
    };

    loadContent();

    return () => {
      active = false;
    };
  }, [selectedProviderId]);

  const buildAccessArray = (accessType: SubscriptionAccessType, selectedIds: number[]): number[] => {
    if (accessType === "all") return [];
    if (accessType === "none") return [-1];
    return selectedIds;
  };

  const isFormValid = (): boolean => {
    if (!selectedProviderId) return false;
    if (portfolioAccess === "specific" && selectedPortfolioIds.length === 0) return false;
    if (basketAccess === "specific" && selectedBasketIds.length === 0) return false;
    return true;
  };

  const togglePortfolio = (id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const toggleBasket = (id: number) => {
    setSelectedBasketIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!selectedProviderId || !isFormValid()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit(selectedProviderId, {
        portfolios: buildAccessArray(portfolioAccess, selectedPortfolioIds),
        baskets: buildAccessArray(basketAccess, selectedBasketIds),
        isSubscribedExternallyToProvider: isExternal,
      });
    } catch (err) {
      console.error("Add investor subscription error:", err);
      setSubmitError(err instanceof Error ? err.message : "Failed to add subscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AdminModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Add Provider Subscription"
      closeDisabled={submitting}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormValid() || submitting}
            className="flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Subscription
          </button>
        </div>
      }
    >
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Provider
              </label>
              <select
                aria-label="Provider"
                value={selectedProviderId ?? ""}
                onChange={(event) => setSelectedProviderId(event.target.value ? Number(event.target.value) : null)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface dark:text-white"
              >
                <option value="">Select a provider</option>
                {availableProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name}
                  </option>
                ))}
              </select>
              {availableProviders.length === 0 && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  This investor already has subscriptions for all loaded providers.
                </p>
              )}
            </div>

            <SubscriptionAccessEditor
              title="Portfolio Access"
              name="investorPortfolioAccess"
              access={portfolioAccess}
              setAccess={setPortfolioAccess}
              items={providerPortfolios}
              selectedIds={selectedPortfolioIds}
              toggleItem={togglePortfolio}
              itemLabel="portfolios"
              contentLoading={contentLoading}
            />

            <SubscriptionAccessEditor
              title="Basket Access"
              name="investorBasketAccess"
              access={basketAccess}
              setAccess={setBasketAccess}
              items={providerBaskets}
              selectedIds={selectedBasketIds}
              toggleItem={toggleBasket}
              itemLabel="baskets"
              contentLoading={contentLoading}
            />

            <label className="mb-4 flex items-start gap-3">
              <input
                type="checkbox"
                checked={isExternal}
                onChange={(event) => setIsExternal(event.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  External subscription
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Enabled for admin-provisioned or imported subscriptions.
                </span>
              </span>
            </label>

            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                {submitError}
              </div>
            )}
    </AdminModalShell>
  );
};

export default AddInvestorSubscriptionModal;
